import { Amount } from './amount.model';
import { CartItem } from './cart-item.model';

export interface ProductDetail<T> {
  name?: string;
  value: T;
  label?: string;
}

export type ProductDetailValue = string | string[] | boolean | number;

export enum ProductDetailsKey {
  CATEGORY_NOTE = 'CATEGORY_NOTE',
  CATEGORY_STARTING_PRICE_FIELD = 'CATEGORY_STARTING_PRICE_FIELD',
  CERTIFICATE_NOTE = 'CERTIFICATE_NOTE',
  CONTAINER_SIZE = 'CONTAINER_SIZE',
  CONTENT_RESTRICTION_NOTE = 'CONTENT_RESTRICTION_NOTE',
  CONTAINER_TYPE_RES = 'CONTAINER_TYPE_RES',
  DELIVERY_NOTE = 'DELIVERY_NOTE',
  DESCRIPTION = 'DESCRIPTION',
  DETAILED_DESCRIPTION = 'DETAILED_DESCRIPTION',
  FOR_BUSINESSES_NOTE = 'FOR_BUSINESSES_NOTE',
  FOR_HOUSEHOLDS_NOTE = 'FOR_HOUSEHOLDS_NOTE',
  INCLUDES_NOTE = 'INCLUDES_NOTE',
  MEASUREMENTS = 'MEASUREMENTS',
  PALLET_INCLUDES_NOTE = 'PALLET_INCLUDES_NOTE',
  PARENT_SERVICE_GROUP = 'PARENT_SERVICE_GROUP',
  PRODUCT_FAMILY_NAME = 'PRODUCT_FAMILY_NAME',
  PRODUCT_ORDER = 'PRODUCT_ORDER',
  SERVICE_GROUP = 'SERVICE_GROUP',
  SERVICE_TYPE = 'SERVICE_TYPE',
  SHORT_NAME = 'SHORT_NAME',
  SIZE_COMPARISON_NOTE = 'SIZE_COMPARISON_NOTE',
  SIZE_DESCRIPTION = 'SIZE_DESCRIPTION',
  SITE_RESTRICTION_NOTE = 'SITE_RESTRICTION_NOTE',
  STARTING_PRICE_FIELD = 'STARTING_PRICE_FIELD',
  TITLE = 'TITLE',
}

export type StringProductDetailsKey = 'SERVICE_FAMILY';

export type ProductDetails = Partial<
  Record<ProductDetailsKey, ProductDetail<string | number>> &
    Record<StringProductDetailsKey, ProductDetail<string>> &
    Record<'GIS_SERVICE_AREA_TYPES', ProductDetail<string[]>> &
    // possible defect. when coming from the cart, this is an array, but original category details is a single string
    // but as far as I can tell, we don't use these values anywhere anyway yet.
    Record<'SERVICE_LOCATOR_CODE', ProductDetail<string | string[]>> &
    Record<CartExclusionsKey, ProductDetail<boolean>> &
    // possible defect. res erecycling has this as a string when the value is 'True' - really should be a boolean
    // but as far as I can tell, we don't use these values anywhere anyway yet.
    Record<
      'ALLOW_PURCHASE_WITHOUT_DIVISION' | 'ALLOW_PURCHASE_BY_EXISTING_CUSTOMER',
      ProductDetail<boolean | string>
    > &
    // possible defect. sometimes this is a number as a string, sometimes a number - really should be a number
    Record<'MAX_QUANTITY', ProductDetail<number | string>>
>;

export interface ProductOptionChoice {
  name: string;
  code: string;
}

export enum ProductOptionCode {
  CASTERS_FLAG = 'CASTERS_FLAG',
  COMPACTOR_FLAG = 'COMPACTOR_FLAG',
  CONTAINER_TYPE_LGC = 'CONTAINER_TYPE_LGC',
  CONTAINER_TYPE_RES = 'CONTAINER_TYPE_RES',
  CONTAINER_TYPE_SMC = 'CONTAINER_TYPE_SMC',
  CONTRACT_LENGTH = 'CONTRACT_LENGTH',
  ENCLOSURE_FLAG = 'ENCLOSURE_FLAG',
  LOCK_FLAG = 'LOCK_FLAG',
  SERVICE_FREQUENCY = 'SERVICE_FREQUENCY',
}

export interface ProductOption {
  name: string;
  code: ProductOptionCode;
  choices: ProductOptionChoice[];
}

export type ProductOptions = Partial<Record<ProductOptionCode, ProductOption>>;

export interface ProductQuantityPrice {
  quantity: number;
  initialPrice?: Amount;
  monthlyPrices?: {
    year1: Amount;
    year2: Amount;
    year3: Amount;
  };
  monthlyYear1Price?: Amount;
  monthlyYear2Price?: Amount;
  monthlyYear3Price?: Amount;
}

export enum BooleanProductSkuDetailKey {
  MONTHLY_FRF_FEE_APPLIES = 'MONTHLY_FRF_FEE_APPLIES',
  MONTHLY_ERF_FEE_APPLIES = 'MONTHLY_ERF_FEE_APPLIES',
  MONTHLY_ADMIN_FEE_APPLIES = 'MONTHLY_ADMIN_FEE_APPLIES',
  INITIAL_FRF_FEE_APPLIES = 'INITIAL_FRF_FEE_APPLIES',
  INITIAL_ERF_FEE_APPLIES = 'INITIAL_ERF_FEE_APPLIES',
  INITIAL_ADMIN_FEE_APPLIES = 'INITIAL_ADMIN_FEE_APPLIES',
}

export enum NumericProductSkuDetailKey {
  WASHOUT = 'WASHOUT',
  RENEWAL_TERM = 'RENEWAL_TERM',
  REMOVAL = 'REMOVAL',
  RELOCATE = 'RELOCATE',
  OVERAGE_PRICE = 'OVERAGE_PRICE',
  MOVE_FEES = 'MOVE_FEES',
  INCLUDED_TONS = 'INCLUDED_TONS',
  EXTRA_YARDS = 'EXTRA_YARDS',
  EXTRA_PICKUP = 'EXTRA_PICKUP',
  EXTRA_LIFTS = 'EXTRA_LIFTS',
  ES_PRICE = 'ES_PRICE',
  ES_DAYS = 'ES_DAYS',
  DRY_RUN = 'DRY_RUN',
  DELIVERY = 'DELIVERY',
  CGROUP3_WEEKLY_LIFTS = 'CGROUP3_WEEKLY_LIFTS',
  CGROUP2_WEEKLY_LIFTS = 'CGROUP2_WEEKLY_LIFTS',
  CGROUP1_WEEKLY_LIFTS = 'CGROUP1_WEEKLY_LIFTS',
}

export enum StringProductSkuDetailKey {
  CGROUP3_CONTAINER_SIZE = 'CGROUP3_CONTAINER_SIZE',
  CGROUP2_CONTAINER_TYPE = 'CGROUP2_CONTAINER_TYPE',
  CGROUP2_CONTAINER_SIZE = 'CGROUP2_CONTAINER_SIZE',
  CGROUP1_CONTAINER_TYPE = 'CGROUP1_CONTAINER_TYPE',
  CGROUP1_CONTAINER_SIZE = 'CGROUP1_CONTAINER_SIZE',
  CGROUP1_DISPOSAL = 'CGROUP1_DISPOSAL',
  TERMS_IDENTIFIER = 'TERMS_IDENTIFIER',
  ES_CODE = 'ES_CODE',
  FOR_HOUSEHOLDS_NOTE = 'FOR_HOUSEHOLDS_NOTE',
  FOR_BUSINESSES_NOTE = 'FOR_BUSINESSES_NOTE',
}

export enum CartExclusionsKey {
  DISALLOW_REMOVE_FROM_CART = 'DISALLOW_REMOVE_FROM_CART',
  EXCLUDE_FROM_CART_PAGE = 'EXCLUDE_FROM_CART_PAGE',
  EXCLUDE_FROM_CART_QUANTITY = 'EXCLUDE_FROM_CART_QUANTITY',
  EXCLUDE_FROM_TERMS_AND_CONDITIONS = 'EXCLUDE_FROM_TERMS_AND_CONDITIONS',
}

export type ProductSkuDetails = Partial<
  Record<CartExclusionsKey, ProductDetail<boolean>> &
    Record<BooleanProductSkuDetailKey, ProductDetail<boolean>> &
    Record<NumericProductSkuDetailKey, ProductDetail<number>> &
    Record<StringProductSkuDetailKey, ProductDetail<string>>
>;

export interface ProductItem {
  id?: string;
}

export interface ShoppingProductSkuResponse {
  productSku: ProductSku;
  constituentProductSkus?: ProductSku[];
}

export interface ProductSku {
  code: string;
  prices?: ProductQuantityPrice[];
  quantityPrices?: ProductQuantityPrice[];
  maxQuantity?: number;
  // SMC Container has a ProductSku object as one of it's items - 'SMC_DEL_x'
  items?: ProductItem[];
  details?: ProductSkuDetails;
}

export interface Product {
  $$hashKey?: string;
  title?: string;
  name?: string;
  code?: string;
  categoryCode?: string;
  isDefault?: boolean;
  isRecommended?: boolean;
  details?: ProductDetails;
  options?: ProductOptions | string[] | ProductOption[];
  constituents?: Product[];
  prices?: ProductQuantityPrice;
  price?: null | string | number;
  priceAmount?: null | number;
  priceType?: null | string;
  startingPrice?: Amount;
  startingPriceType?: string;
  purchasable?: boolean;
  serviceName?: string;
  sku?: ProductSku;
  skus?: ProductSku[];
  groupName?: string;
  products?: Product[];
  quantity?: number;
  cartEstimate?: CartItem;
}

interface RecommendedAdditionalContainerDetails {
  SERVICE_GROUP: string;
}

export interface RecommendedAdditionalContainer {
  details: RecommendedAdditionalContainerDetails;
  quantity: number;
}

export interface RecommendedProduct {
  title?: string;
  code?: string;
  details?: ProductDetails | ProductDetails[];
  exactPrice?: boolean;
  id: string;
  imagePath?: string;
  isRecommendedProduct?: boolean;
  price?: number;
  priceAmount?: number;
  priceType?: string;
  purchasable?: boolean;
  recommendedProductId?: string;
  product?: RecommendedProduct;
  options?: ProductOptions;
  name?: string;
  categoryCode?: string;
  constituents?: Product[];
  items?: RecommendedAdditionalContainer[];
  prices?: ProductQuantityPrice[];
  quantity?: number;
  skuCode?: string;
}

export interface QuantityChanged {
  quantity: number;
  index: number;
}

export enum UserOptionsKey {
  COMPACTOR_FLAG = 'COMPACTOR_FLAG',
  CONTAINER_TYPE_RES = 'CONTAINER_TYPE_RES',
  SERVICE_FREQUENCY = 'SERVICE_FREQUENCY',
  CASTERS_FLAG = 'CASTERS_FLAG',
  ENCLOSURE_FLAG = 'ENCLOSURE_FLAG',
  LOCK_FLAG = 'LOCK_FLAG',
  CONTRACT_LENGTH = 'CONTRACT_LENGTH',
  REQUESTED_COLLECTION_DATE = 'REQUESTED_COLLECTION_DATE',
  DELIVERY_AND_PLACEMENT_DETAILS = 'DELIVERY_AND_PLACEMENT_DETAILS',
  ADDED_FROM_RECOMMENDED = 'ADDED_FROM_RECOMMENDED',
  ADDED_FROM_CATEGORY_CODE = 'ADDED_FROM_CATEGORY_CODE',
  ESTIMATED_DELIVERY_DATE = 'ESTIMATED_DELIVERY_DATE',
  REQUESTED_SERVICE_START_DATE = 'REQUESTED_SERVICE_START_DATE',
  REQUESTED_SERVICE_DAYS = 'REQUESTED_SERVICE_DAYS',
}

export type UserOptions = Partial<Record<UserOptionsKey, string>>;

export interface UpdateUserOption {
  lockBar: string;
  enclosure: string;
  casters: string;
}

export interface SpecificProductAmounts {
  lgcQty?: number;
  lgcTotal?: number;
  resQty?: number;
  resTotal?: number;
  smcQty?: number;
  smcTotal?: number;
}
